import React, { useState, useEffect, Fragment } from 'react'
import classNames from 'classnames'
import { useRouteMatch } from 'react-router-dom'
import Drawer from '@material-ui/core/Drawer'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { getNextLink } from '../../Utils/Link'
import { IntegrationsIcon } from '../../Assets/Images/icons/IntegrationsIcon'
import SidebarDashboardIcon from '../../Assets/Images/icons/sidebarDashboard'
import { DMSidebarIcon } from '../../Assets/Images/icons/DMSidebarIcon'
import { H2HSidebarIcon } from '../../Assets/Images/icons/H2HSidebarIcon'
import { D2DSidebarIcon } from '../../Assets/Images/icons/D2DSidebarIcon'
import SidebarHomeIcon from '../../Assets/Images/icons/sidebarHome'
import SidebarHelpIcon from '../../Assets/Images/icons/sidebarHelp'
import { PATHS } from '../../Config/Routing/paths'
import { useFeatureFlags } from '../../Context/FeatureFlagsContext'
import { authorization } from '../../Module/Auth/Auth'
import SidebarSettingsIcon from '../../Assets/Images/icons/sidebarSettings'
import SelfServeClientChat from '../SelfServeClientChat/container'
import { EDDM_SIGNUP } from '../../shared/constants'
import { useStyles } from './styles'
import MenuItem from './MenuItem'
import AimIcon from '../../Assets/Images/icons/aimIcon'
import MarketingTools from '../../Assets/Images/icons/MarketingTools'
import { SidebarProvider } from './SidebarContext'
import SidebarCreativeIcon from '../../Assets/Images/icons/sidebarCreative'
import AudienceAndVariableIcon from '../../Assets/Images/icons/audienceAndVariableIcon'
import { COUNTRY_CODE } from '../../constants'

import { navigateToPreloadedSite } from '../../Components/IframePreloader'
import useCookieState from 'cookie-state'

const ITEM_TYPES = {
  HOME: 'HOME',
  DASHBOARD: 'DASHBOARD',
  ACTIVE_CAMPAIGNS: 'ACTIVE_CAMPAIGNS',
  DIRECT_MAIL: 'DIRECT_MAIL',
  DOOR_TO_DOOR: 'DOOR_TO_DOOR',
  HAND_TO_HAND: 'HAND_TO_HAND',
  INSERTS: 'INSERTS',
  AUDIENCE_AND_VARIABLE: 'AUDIENCE_AND_VARIABLE',
  INTEGRATIONS: 'INTEGRATIONS',
  HELP: 'HELP',
  SETTINGS: 'SETTINGS',
  MARKETING_TOOLS: 'MARKETING_TOOLS',
  EDDM: 'EDDM',
  DESIGN_MANAGEMENT: 'DESIGN_MANAGEMENT',
}

const getInitialActiveClientCampaignsElement = (history, isEddmSignup) => {
  if (history.location.pathname.endsWith(`${PATHS.campaignsTypes.d2d}`)) {
    return ITEM_TYPES.DOOR_TO_DOOR
  }

  if (history.location.pathname.endsWith(PATHS.campaignsTypes.h2h)) {
    return ITEM_TYPES.HAND_TO_HAND
  }

  const isDirectMail =
    history.location.pathname.endsWith(PATHS.campaignsTypes.dm) ||
    history.location.pathname.endsWith(PATHS.campaignsTypes.dmEddm) ||
    history.location.pathname.endsWith(PATHS.campaignsTypes.dmAdm) ||
    history.location.pathname.endsWith(PATHS.campaignsTypes.dmWorkflows)

  if (isDirectMail && !isEddmSignup) {
    return ITEM_TYPES.DIRECT_MAIL
  } else if (isDirectMail && isEddmSignup) {
    return ITEM_TYPES.EDDM
  }

  if (history.location.pathname.endsWith(PATHS.campaignsTypes.inserts)) {
    return ITEM_TYPES.INSERTS
  }

  return ITEM_TYPES.DASHBOARD
}

const getInitialActiveElement = ({ history, match }, isEddmSignup) => {
  if (
    history.location.pathname.startsWith(
      `${match.path}${PATHS.dashboard.clientCampaigns}`,
    )
  ) {
    return getInitialActiveClientCampaignsElement(history, isEddmSignup)
  }

  if (history.location.pathname.includes(PATHS.dashboard.designManagement)) {
    return ITEM_TYPES.DESIGN_MANAGEMENT
  }

  if (
    history.location.pathname.startsWith(
      `${match.path}${PATHS.dashboard.clientSettings}`,
    )
  ) {
    return ITEM_TYPES.SETTINGS
  }

  if (
    history.location.pathname.startsWith(
      `${match.path}${PATHS.dashboard.integrations}`,
    )
  ) {
    return ITEM_TYPES.INTEGRATIONS
  }

  return ITEM_TYPES.HOME
}

const SidebarSelfServe = ({ children, history, user }) => {
  const match = useRouteMatch()
  const classes = useStyles()
  const { isAdmEnabled, isBrazeEnabled } = useFeatureFlags()
  const { client, countryCode } = authorization.getUser()
  const { value: uiData, setValue: setUiData } = useCookieState(
    'ui_data',
    {
      isSidebarOpen: true,
    },
    {
      domain:
        process.env.NODE_ENV === 'development' ? 'localhost' : '.oppizi.com',
      path: '/',
      sameSite: 'strict',
    },
  )

  const open = uiData.isSidebarOpen
  const setOpen = newValue =>
    setUiData(prev => ({ ...prev, isSidebarOpen: newValue }))

  const isUS = countryCode === COUNTRY_CODE.UNITED_STATES
  const isUSSelfServeClient =
    countryCode === COUNTRY_CODE.UNITED_STATES && client.isSelfServeEnabled
  const isChatEnabled = client && client.countryId
  const isQrSignup = client.createdBy === 'qr_signup'
  const isEddmSignup = client.createdBy === EDDM_SIGNUP
  const isCoreClient = !client.isSelfServeEnabled
  const apiAccessEnabled = client.apiAccessEnabled

  // Check if the current route is the Flyer builder (DesignEditor)
  const isFlyerBuilder = history.location.pathname.includes(
    '/design-management/design/',
  )

  const [activeElement, setActiveElement] = useState(
    getInitialActiveElement({ history, match }, isEddmSignup),
  )

  useEffect(() => {
    const newActiveElement = getInitialActiveElement(
      { history, match },
      isEddmSignup,
    )
    if (newActiveElement !== activeElement) {
      setActiveElement(newActiveElement)
    }
  }, [history.location.pathname])

  const NAV_ITEMS = {
    ACTIVE_CAMPAIGNS: {
      icon: (
        <SidebarDashboardIcon
          isActive={ITEM_TYPES.DASHBOARD === activeElement}
        />
      ),
      name: 'Active campaigns',
      type: ITEM_TYPES.DASHBOARD,
      href: `${match.path}${PATHS.dashboard.clientCampaigns}${
        client ? `/${client.id}` : ''
      }${PATHS.campaignsTypes.active}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.DASHBOARD && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.DASHBOARD)
        }
      },
    },
    DIRECT_MAIL: {
      icon: (
        <DMSidebarIcon isActive={ITEM_TYPES.DIRECT_MAIL === activeElement} />
      ),
      name: 'Direct mail',
      type: ITEM_TYPES.DIRECT_MAIL,
      href: `${match.path}${PATHS.dashboard.clientCampaigns}${
        client ? `/${client.id}` : ''
      }${PATHS.campaignsTypes.dm}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.DIRECT_MAIL && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.DIRECT_MAIL)
        }
      },
    },
    US_DIRECT_MAIL: {
      icon: <DMSidebarIcon />,
      name: 'Direct mail',
      type: ITEM_TYPES.DIRECT_MAIL,
      href: isAdmEnabled
        ? ''
        : `${match.path}${PATHS.dashboard.clientCampaigns}${
            client ? `/${client.id}` : ''
          }${PATHS.campaignsTypes.dmEddm}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.DIRECT_MAIL && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.DIRECT_MAIL)
        }
      },
      subLinks: isAdmEnabled
        ? (() => {
            const links = [
              {
                name: 'Addressed Direct Mail',
                href: `${match.path}${PATHS.dashboard.clientCampaigns}${
                  client ? `/${client.id}` : ''
                }${PATHS.campaignsTypes.dmAdm}`,
              },
            ]

            if (isBrazeEnabled) {
              links.push({
                name: 'Direct Mail Workflow',
                href: getNextLink(
                  `${PATHS.main.dashboard}${client ? `/${client.id}` : ''}${
                    PATHS.campaignsTypes.dmWorkflows
                  }`,
                ),
              })
            }

            links.push({
              name: 'Every Door (EDDM)',
              href: `${match.path}${PATHS.dashboard.clientCampaigns}${
                client ? `/${client.id}` : ''
              }${PATHS.campaignsTypes.dmEddm}`,
            })

            return links
          })()
        : null,
    },
    EDDM: {
      icon: <DMSidebarIcon />,
      name: 'EDDM',
      type: ITEM_TYPES.EDDM,
      href: `${match.path}${PATHS.dashboard.clientCampaigns}${
        client ? `/${client.id}` : ''
      }${PATHS.campaignsTypes.dmEddm}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.EDDM && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.EDDM)
        }
      },
    },
    FLYERING: {
      icon: (
        <H2HSidebarIcon isActive={ITEM_TYPES.HAND_TO_HAND === activeElement} />
      ),
      name: 'Flyering',
      type: ITEM_TYPES.HAND_TO_HAND,
      href: `${match.path}${PATHS.dashboard.clientCampaigns}${
        client ? `/${client.id}` : ''
      }${PATHS.campaignsTypes.h2h}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.HAND_TO_HAND && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.HAND_TO_HAND)
        }
      },
    },
    DOOR_TO_DOOR: {
      icon: (
        <D2DSidebarIcon isActive={ITEM_TYPES.DOOR_TO_DOOR === activeElement} />
      ),
      name: 'Door to door',
      type: ITEM_TYPES.DOOR_TO_DOOR,
      href: `${match.path}${PATHS.dashboard.clientCampaigns}${
        client ? `/${client.id}` : ''
      }${PATHS.campaignsTypes.d2d}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.DOOR_TO_DOOR && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.DOOR_TO_DOOR)
        }
      },
    },
    INSERTS: {
      icon: <AimIcon isActive={ITEM_TYPES.INSERTS === activeElement} />,
      name: 'Inserts marketing',
      type: ITEM_TYPES.INSERTS,
      href: `${match.path}${PATHS.dashboard.clientCampaigns}${
        client ? `/${client.id}` : ''
      }${PATHS.campaignsTypes.inserts}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.INSERTS && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.INSERTS)
        }
      },
    },
    AUDIENCE_AND_VARIABLE: {
      icon: (
        <AudienceAndVariableIcon
          isActive={ITEM_TYPES.AUDIENCE_AND_VARIABLE === activeElement}
        />
      ),
      name: 'Audience & Variable',
      type: ITEM_TYPES.AUDIENCE_AND_VARIABLE,
      href: getNextLink(
        `${PATHS.main.dashboard}${client ? `/${client.id}` : ''}${
          PATHS.dashboard.audienceAndVariable
        }`,
      ),

      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (
          activeElement !== ITEM_TYPES.AUDIENCE_AND_VARIABLE &&
          !isCtrlClick
        ) {
          setActiveElement(ITEM_TYPES.AUDIENCE_AND_VARIABLE)
        }

        const baseUrl = getNextLink(
          `${PATHS.main.dashboard}${client ? `/${client.id}` : ''}${
            PATHS.dashboard.audienceAndVariable
          }`,
        )

        navigateToPreloadedSite(baseUrl)
      },
    },
    INTEGRATIONS: {
      icon: (
        <IntegrationsIcon
          isActive={ITEM_TYPES.INTEGRATIONS === activeElement}
        />
      ),
      name: 'Integrations',
      type: ITEM_TYPES.INTEGRATIONS,
      href: getNextLink(
        `${PATHS.main.dashboard}${client ? `/${client.id}` : ''}${
          PATHS.dashboard.integrations
        }`,
      ),

      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.INTEGRATIONS && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.INTEGRATIONS)
        }

        const baseUrl = getNextLink(
          `${PATHS.main.dashboard}${client ? `/${client.id}` : ''}${
            PATHS.dashboard.integrations
          }`,
        )

        navigateToPreloadedSite(baseUrl)
      },
    },
    MARKETING_TOOLS: {
      icon: <MarketingTools />,
      name: 'Marketing Tools',
      type: ITEM_TYPES.MARKETING_TOOLS,
      href: '',
      onClick: () => {
        const baseUrl = `${PATHS.external.tools}/home?client_id=${
          user.client.id
        }`
        const url = baseUrl
        window.open(url, '_self')
      },
    },
    DESIGN_MANAGEMENT: {
      icon: (
        <SidebarCreativeIcon
          isActive={ITEM_TYPES.DESIGN_MANAGEMENT === activeElement}
        />
      ),
      name: 'Creative',
      type: ITEM_TYPES.DESIGN_MANAGEMENT,
      href: `${match.path}${PATHS.dashboard.designManagement}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.DESIGN_MANAGEMENT && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.DESIGN_MANAGEMENT)
        }
      },
    },
    SETTINGS: {
      icon: (
        <SidebarSettingsIcon isActive={ITEM_TYPES.SETTINGS === activeElement} />
      ),
      name: 'Settings',
      type: ITEM_TYPES.SETTINGS,
      href: `${match.path}${PATHS.dashboard.clientSettings}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.SETTINGS && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.SETTINGS)
        }
      },
    },
    HOME: {
      icon: <SidebarHomeIcon isActive={ITEM_TYPES.HOME === activeElement} />,
      name: 'Home',
      type: ITEM_TYPES.HOME,
      href: `${match.path}${PATHS.dashboard.home}`,
      onClick: e => {
        const isCtrlClick = e.ctrlKey || e.metaKey
        if (activeElement !== ITEM_TYPES.HOME && !isCtrlClick) {
          setActiveElement(ITEM_TYPES.HOME)
        }
      },
    },
    HELP_CENTER: {
      icon: <SidebarHelpIcon isActive={ITEM_TYPES.HELP === activeElement} />,
      name: 'Help Center',
      type: ITEM_TYPES.HELP,
      href: '',
      onClick: () => {
        window.open('https://selfserve.oppizi.com/', '_blank')
      },
    },
  }

  const getItems = () => {
    const items = []

    // Add Home first for self-serve enabled clients
    if (client.isSelfServeEnabled) {
      items.push(NAV_ITEMS.HOME)
    }

    items.push(NAV_ITEMS.ACTIVE_CAMPAIGNS)
    if (isEddmSignup) {
      items.push(NAV_ITEMS.EDDM)
    } else {
      if (isUS && client.isSelfServeEnabled) {
        items.push(NAV_ITEMS.US_DIRECT_MAIL)
      }

      items.push(NAV_ITEMS.FLYERING, NAV_ITEMS.DOOR_TO_DOOR, NAV_ITEMS.INSERTS)

      if (isUSSelfServeClient) {
        items.push(NAV_ITEMS.AUDIENCE_AND_VARIABLE)
      }

      if (client.isSelfServeEnabled || (isCoreClient && apiAccessEnabled)) {
        items.push(NAV_ITEMS.INTEGRATIONS)
      }

      if (isQrSignup) {
        items.push(NAV_ITEMS.MARKETING_TOOLS)
      }
    }

    items.push(NAV_ITEMS.DESIGN_MANAGEMENT)
    items.push(NAV_ITEMS.SETTINGS)

    if (client.isSelfServeEnabled) {
      items.push(NAV_ITEMS.HELP_CENTER)
    }

    return items
  }

  return (
    <Fragment>
      <div className={classes.root}>
        <div
          className={classNames(
            classes.wrapperSidebar,
            'main_root_sidebar_wrapper',
          )}
        >
          <div className={classes.toolbar}>
            {open ? (
              <div className={classes.button} onClick={() => setOpen(false)}>
                <ChevronLeftIcon />
              </div>
            ) : (
              <div className={classes.button} onClick={() => setOpen(true)}>
                <ChevronRightIcon />
              </div>
            )}
          </div>
          <Drawer
            variant="permanent"
            className={classNames(classes.drawer, {
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            })}
            classes={{
              paper: classNames({
                [classes.drawerOpen]: open,
                [classes.drawerClose]: !open,
              }),
            }}
          >
            <div
              className={classNames(
                classes.itemsWrapper,
                'makeStyles-itemsWrap',
              )}
            >
              {getItems().map(item => (
                <MenuItem
                  key={item.name}
                  item={item}
                  isActive={activeElement === item.type}
                  open={open}
                  setOpen={setOpen}
                  classes={classes}
                  currentPath={history.location.pathname}
                  onSetActiveElement={setActiveElement}
                />
              ))}
            </div>
          </Drawer>
        </div>
        <SidebarProvider setOpen={setOpen}>
          <main className={classNames(classes.content)}>{children}</main>
        </SidebarProvider>
      </div>

      {isChatEnabled && !isFlyerBuilder ? (
        <SelfServeClientChat
          user={user}
          client={user.client}
          countryId={String(user.client.countryId)}
        />
      ) : null}
    </Fragment>
  )
}

export default SidebarSelfServe
